import React, { useState, useCallback } from 'react';
import Code from '../../Code/Code';
import EmbedCodeCopyButton from '../../EmbedCodeCopyButton/EmbedCodeCopyButton';
import { UnControlled as ReactCodeMirror } from 'react-codemirror2';
import ComboBox from '../../../../components/ComboBox/ComboBox';
import getHostUrlForIntegrations from '../../../../../services/mini/getHostUrlForIntegrations';

const htmlCodeMirrorOptions = {
    lineNumbers: false,
    readOnly: true,
    mode: {
        name: 'javascript',
    },
    scrollbarStyle: null,
    cursorScrollMargin: -1000,
};

const ReactNativeCliInstallCode = 'npm install -g react-native-cli';

const ReactNativeCocoaInstallCode = 'sudo gem install cocoapods';

const ReactNativeAlanInstallCode = 'npm i @alan-ai/alan-sdk-react-native';

const ReactNativeAlanImportCode = 'import { AlanView } from \'@alan-ai/alan-sdk-react-native\';';

function getReactNativeCode(alanKey, codeMode) {
  if (codeMode === 'simple') {
    return `render() {
  /// Add Alan Button with project key from Alan AI Studio
  <AlanView projectid={'${alanKey}'} host={'${getHostUrlForIntegrations()}'}/>
}

componentDidMount() {
  /// Handle commands from Alan AI Studio
  alanEventEmitter.addListener('onCommand', (data) => {
    console.log(\`onCommand: \${JSON.stringify(data)}\`);
  });
}

componentWillUnmount() {
  alanEventEmitter.removeAllListeners('onCommand');
}
`;
  } else {
    return `render() {
  /// Add Alan Button with project key and additional params
  <AlanView projectid={'${alanKey}'} host={'${getHostUrlForIntegrations()}'} authData={{"data":"your auth data if needed"}}/>
}

componentDidMount() {
  /// Handle commands from Alan AI Studio
  alanEventEmitter.addListener('onCommand', (data) => {
    console.log(\`onCommand: \${JSON.stringify(data)}\`);
  });

  /// Handle button state
  alanEventEmitter.addListener('onButtonState', (state) => {
    console.log(\`onButtonState: \${JSON.stringify(state)}\`);
  });

  /// Handle events
  alanEventEmitter.addListener('onEvent', (payload) => {
    console.log(\`onEvent: \${JSON.stringify(payload)}\`);
  });
}

componentWillUnmount() {
  alanEventEmitter.removeAllListeners('onCommand');
  alanEventEmitter.removeAllListeners('onButtonState');
  alanEventEmitter.removeAllListeners('onEvent');
}

/// Activate Alan Button programmatically
activate() {
  AlanManager.activate();
}

/// Deactivate Alan Button programmatically
deactivate() {
  AlanManager.deactivate();
}

/// Play any text via Alan Button
playText() {
  /// Provide text as string param
  AlanManager.playText("Hi");
}

/// Execute any command locally
playCommand() {
  /// Provide any params with json
  AlanManager.playCommand({"action":"openSomePage"})
}

/// Call projectAPI.funcName from Alan AI Studio script
callProjectApi() {
  /// Provide any params with json
  AlanManager.callProjectApi(
    'script::funcName', {"data":"your data"},
      (error, result) => {
      if (error) {
        console.error(error);
      } else {
        console.log(result);
      }
    },
  )
}

/// Set up a visual state
setVisualState() {
  /// Provide any params with json
  AlanManager.setVisualState({"data":"your data"});
}
    `;
  }
}

const codeModes = [
  { name: 'Simple Example', key: 'simple' },
  { name: 'Advanced Example', key: 'advanced' }
];

export function ReactNativeTab({ alanKey }) {

    const [codeMode, setCodeMode] = useState('simple');

    const changeCodeModeCb = useCallback((codeModeObj) => {
      setCodeMode(codeModeObj.key);
    }, []);

    return <div className="embed-code-tab">
        <div className="embed-code-tab_line">
            <div className="embed-code-tab_description">
            </div>
            <div className="embed-code-tab_dash"></div>
            <div className="embed-code-tab_code">
                <div className="embed-code-tab_not-highlighted-code">
                    <Code>
                        {
                            '\n'
                        }
                    </Code>
                </div>
            </div>

        </div>
        <ComboBox values={codeModes}
            valueKey="key"
            valueName="name"
            classes="code-mode-combo-box light-blue-with-border"
            selectedValueId={codeMode}
            onValueSelect={changeCodeModeCb}
            renderItem={(codeModeItem) => {
                return <div style={{ paddingLeft: '8px' }}
                    key={codeModeItem.key}>
                    {codeModeItem.name}
                </div>;
            }} />
        <div className="embed-code-tab_line">
            <div className="embed-code-tab_description embed-code-desc4">
                Install React Native CLI
            </div>
            <div className="embed-code-tab_dash embed-code-desc4">
                <div className="embed-code-tab_separator" />
            </div>
            <div className="embed-code-tab_code">
                <div className="embed-code-tab_highlighted-code">
                    <Code highlighted category="ReactNative" type="code">
                        {ReactNativeCliInstallCode}
                    </Code>
                    <div className="embed-code-tab_copy">
                        <EmbedCodeCopyButton code={ReactNativeCliInstallCode} />
                    </div>
                </div>
            </div>
        </div>
        <div className="embed-code-tab_line">
            <div className="embed-code-tab_description embed-code-desc4">
                Install CocoaPods
            </div>
            <div className="embed-code-tab_dash embed-code-desc4">
                <div className="embed-code-tab_separator" />
            </div>
            <div className="embed-code-tab_code">
                <div className="embed-code-tab_highlighted-code">
                    <Code highlighted category="ReactNative" type="code">
                        {ReactNativeCocoaInstallCode}
                    </Code>
                    <div className="embed-code-tab_copy">
                        <EmbedCodeCopyButton code={ReactNativeCocoaInstallCode} />
                    </div>
                </div>
            </div>
        </div>
        <div className="embed-code-tab_line">
            <div className="embed-code-tab_description embed-code-desc4">
                Add the React Native plugin to the app
            </div>
            <div className="embed-code-tab_dash embed-code-desc4">
                <div className="embed-code-tab_separator" />
            </div>
            <div className="embed-code-tab_code">
                <div className="embed-code-tab_highlighted-code">
                    <ReactCodeMirror
                        value={ReactNativeAlanInstallCode}
                        options={htmlCodeMirrorOptions} />
                    <div className="embed-code-tab_copy">
                        <EmbedCodeCopyButton code={ReactNativeAlanInstallCode} />
                    </div>
                </div>
            </div>
        </div>
        <div className="embed-code-tab_line">
            <div className="embed-code-tab_description embed-code-desc4">
                Import the Alan button
            </div>
            <div className="embed-code-tab_dash embed-code-desc4">
                <div className="embed-code-tab_separator" />
            </div>
            <div className="embed-code-tab_code">
                <div className="embed-code-tab_highlighted-code">
                    <ReactCodeMirror
                        value={ReactNativeAlanImportCode}
                        options={htmlCodeMirrorOptions} />
                    <div className="embed-code-tab_copy">
                        <EmbedCodeCopyButton code={ReactNativeAlanImportCode} />
                    </div>
                </div>
            </div>
        </div>
        <div className="embed-code-tab_line">
            <div className="embed-code-tab_description embed-code-desc4">
                Add Alan button
            </div>
            <div className="embed-code-tab_dash embed-code-desc4">
                <div className="embed-code-tab_separator" />
            </div>
            <div className="embed-code-tab_code">
                <div className="embed-code-tab_highlighted-code">
                    <ReactCodeMirror
                        value={getReactNativeCode(alanKey, codeMode)}
                        options={htmlCodeMirrorOptions} />
                    <div className="embed-code-tab_copy">
                        <EmbedCodeCopyButton code={getReactNativeCode(alanKey, codeMode)} />
                    </div>
                </div>
            </div>
        </div>
    </div>;
}
